<template>
  <BaseTemplate
    class="player-template"
    data-test="player-template"
    header-min-height="var(--player-header-height)"
    :toasts="toasts"
    @clickToast="handleClickToast"
  >
    <template #header>
      <PlayerHeader
        :is-auth="isAuth"
        :links="headerLinks"
        :user="user"
        :header-min-height="headerMinHeight"
        :header-logo-link="headerLogoLink"
        @toggleMobileMenu="handleToggleMobileMenu"
        @add-event="emit('add-event')"
      />
    </template>
    <template #default>
      <slot />
    </template>
    <template #main-sibling>
      <MobileMenu
        v-model:is-open="mobileIsOpen"
        :menu-items="headerLinks"
        @add-event="emit('add-event')"
      />
    </template>
    <template #footer>
      <Footer :links="footerLinks" :social-links="footerSocialLinks" />
    </template>
  </BaseTemplate>
</template>

<script lang="ts" setup>
import { defineComponent, defineEmits, defineProps, PropType, ref } from 'vue'
import { onBeforeRouteUpdate, RouteLocationRaw } from 'vue-router'

import PlayerHeader from '../PlayerHeader/PlayerHeader.vue'
import MobileMenu from '@base/components/MobileMenu/MobileMenu.vue'
import Footer from '@base/components/Footer/Footer.vue'
import BaseTemplate from '@base/components/BaseTemplate/BaseTemplate.vue'

import { Toast } from '@base/_types/Toast'
import type { PlayerPrimaryNavLink } from '@base/_types/PlayerPrimaryNavLink'

/** Primary nav: routes and optional actions (e.g. Add Event modal). */
type PlayerHeaderLink = PlayerPrimaryNavLink

interface FooterLink {
  label: string
  to: RouteLocationRaw
}

interface SocialLink {
  icon: string
  url: string
}

interface User {
  firstName: string
  lastName: string
  profilePicture?: string
  profilePictureSrcset?: string
  activeMembership?: boolean
  pointsBalance?: number
}

defineComponent({
  name: 'PlayerTemplate',
  components: {
    BaseTemplate,
    Footer,
    MobileMenu,
    PlayerHeader
  }
})

const props = defineProps({
  headerMinHeight: {
    type: String,
    default: '0px'
  },
  headerLinks: {
    type: Array as PropType<PlayerHeaderLink[]>,
    required: true
  },
  isAuth: { type: Boolean, default: false },
  user: { type: Object as PropType<User>, required: true },
  mobileMenuOpen: { type: Boolean, default: false },
  footerLinks: {
    type: Array as PropType<FooterLink[]>,
    default: () => []
  },
  footerSocialLinks: {
    type: Array as PropType<Array<SocialLink>>,
    default: [] as Array<SocialLink>
  },
  headerLogoLink: {
    type: Boolean,
    default: true
  },
  toasts: {
    type: Array as PropType<Toast[]>,
    default: () => []
  }
})

const emit = defineEmits(['clickToast', 'add-event'])

const mobileIsOpen = ref(props.mobileMenuOpen)

const handleToggleMobileMenu = () => {
  mobileIsOpen.value = !mobileIsOpen.value
}
const handleClickToast = (id: string) => {
  emit('clickToast', id)
}
</script>
